
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
   Search, Edit2, Mail, Phone, Building, 
  UserPlus, Clock, Filter, X, Briefcase, Users 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { toast } from 'react-toastify';
import SummaryApi from '../../common/SummaryApi';
import api from '../../common/apiClient';
import { formatDateTime } from '../../helpers/formatDateTime';
import UserListSkeleton from '../../components/SkeletonComponents/UserListSkeleton';
import SelectDropdown from '../../customStyles/SelectDropdown';
import ModernTable from '../../components/ui/ModernTable';


const SecurityUsers = () => {
  const navigate = useNavigate();
  const [allUsers, setAllUsers] = useState([]);
  const [loadingStates, setLoadingStates] = useState({
    users: true,
    roles: true,
    departments: true,
    divisions: true
  });
  
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  const [showFilters, setShowFilters] = useState(false);
  const [roleFilter, setRoleFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [divisionFilter, setDivisionFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [divisions, setDivisions] = useState([]);

  const isLoading = useMemo(() => Object.values(loadingStates).some(l => l), [loadingStates]);

  const updateLoadingState = (key, value) => {
    setLoadingStates(prev => ({ ...prev, [key]: value }));
  };

  const fetchFilterOptions = useCallback(async () => {
    try {
      updateLoadingState('roles', true);
      updateLoadingState('departments', true);
      updateLoadingState('divisions', true);

      const [rolesRes, deptRes, divRes] = await Promise.all([
        api.get(SummaryApi.getAllRoles.url),
        api.get(SummaryApi.getAllDepartments.url),
        api.get(SummaryApi.getAllDivisions.url)
      ]);

      if (rolesRes.data.success) setRoles(rolesRes.data.data || []);
      if (deptRes.data.success) setDepartments(deptRes.data.data || []);
      if (divRes.data.success) setDivisions(divRes.data.data || []);
    } catch (error) {
      console.error('Filter options error:', error);
    } finally {
      updateLoadingState('roles', false);
      updateLoadingState('departments', false);
      updateLoadingState('divisions', false);
    }
  }, []);

  const fetchAllUsers = useCallback(async () => {
    updateLoadingState('users', true);
    try {
      const response = await api.get(SummaryApi.getAllUsers.url);
      if (response.data.success) setAllUsers(response.data.data || []);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      updateLoadingState('users', false);
    }
  }, []);

  useEffect(() => {
    fetchFilterOptions();
    fetchAllUsers();
  }, [fetchFilterOptions, fetchAllUsers]);

  const filteredUsers = useMemo(() => {
    return allUsers.filter(user => {
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
        if (!fullName.includes(searchLower) && 
            !user.email.toLowerCase().includes(searchLower) &&
            !user.userId?.toLowerCase().includes(searchLower)) return false;
      }
      if (roleFilter && user.role !== roleFilter) return false;
      if (departmentFilter && user.departmentId?._id !== departmentFilter) return false;
      if (divisionFilter && user.divisionId?._id !== divisionFilter) return false;
      if (statusFilter) {
        const isActive = statusFilter === 'active';
        if (user.isActive !== isActive) return false;
      }
      return true;
    });
  }, [allUsers, searchTerm, roleFilter, departmentFilter, divisionFilter, statusFilter]);


  // Pagination logic handling "All" string
  const paginatedData = useMemo(() => {
    if (itemsPerPage === 'All') {
      return filteredUsers;
    }
    const startIndex = (currentPage - 1) * Number(itemsPerPage);
    const endIndex = startIndex + Number(itemsPerPage);
    return filteredUsers.slice(startIndex, endIndex);
  }, [filteredUsers, currentPage, itemsPerPage]);

//   const handleAdd = () => navigate(`employee-create`);
//   const handleView = (user) => navigate(`employee-view/${user._id}`);
  const handleEdit = (user) => navigate(`permissions/edit/${user._id}`);

  // Helpers for UI components inside table
  const getInitials = (f, l) => `${f?.[0] || ''}${l?.[0] || ''}`.toUpperCase();
  
  const getRoleBadge = (role) => {
    const colors = {
      owner: 'bg-purple-100 text-purple-700 border-purple-200',
      admin: 'bg-red-100 text-red-700 border-red-200',
      manager: 'bg-blue-100 text-blue-700 border-blue-200',
      default: 'bg-gray-100 text-gray-700 border-gray-200'
    };
    const style = colors[role?.toLowerCase()] || colors.default;
    return <span className={`px-3 py-1 text-xs font-medium rounded-full border ${style}`}>{role}</span>;
  };

  const getStatusBadge = (isActive) => (
    <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
      <span className={`w-1.5 h-1.5 rounded-full mr-1 ${isActive ? 'bg-green-500' : 'bg-red-500'}`}></span>
      {isActive ? 'Active' : 'Inactive'}
    </span>
  );

  // ModernTable Column Configuration
  const columns = [
    {
      label: 'User',
      key: 'user',
      render: (row) => (
        <div className="flex items-center min-w-[200px]">
          {row.profilePicture ? (
            <img src={row.profilePicture} alt="" className="w-10 h-10 rounded-full object-cover" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center text-white font-bold text-sm">
              {getInitials(row.firstName, row.lastName)}
            </div>
          )}
          <div className="ml-3">
            <div className="text-sm font-semibold text-gray-900">{row.firstName} {row.lastName}</div>
            <div className="text-xs text-gray-500">{row.userId}</div>
          </div>
        </div>
      )
    },
    {
      label: 'Contact',
      key: 'email',
      render: (row) => (
        <div className="text-sm">
          <div className="flex items-center text-gray-900">
            <Mail className="h-3 w-3 mr-1 text-gray-400" /> {row.email}
          </div>
          {row.mobile && (
            <div className="flex items-center text-gray-500 mt-1">
              <Phone className="h-3 w-3 mr-1 text-gray-400" /> {row.mobile}
            </div>
          )}
        </div>
      )
    },
    {
      label: 'Dept / Div',
      key: 'departmentId',
      render: (row) => (
        <div className="text-sm">
          <div className="flex items-center font-medium"><Building className="h-3 w-3 mr-1 text-gray-400" /> {row.departmentId?.name || '-'}</div>
          <div className="flex items-center text-gray-500 text-xs mt-1"><Briefcase className="h-3 w-3 mr-1 text-gray-400" /> {row.divisionId?.name || '-'}</div>
        </div>
      )
    },
    {
      label: 'Role',
      key: 'role',
      render: (row) => getRoleBadge(row.role)
    },
    {
      label: 'Status',
      key: 'isActive',
      render: (row) => getStatusBadge(row.isActive)
    },
    {
      label: 'Joining Date',
      key: 'doj',
      render: (row) => <div className="text-sm">{formatDateTime(row.doj, false)}</div>
    },
    {
        label: 'Logs',
        key: 'updatedAt',
        render: (row) => (
            <div className="text-[11px] space-y-0.5">
                <div className="flex items-center"><UserPlus className="h-3 w-3 mr-1" /> {typeof row.createdBy === 'object' ? row.createdBy.firstName : 'System'}</div>
                <div className="flex items-center text-gray-400"><Clock className="h-3 w-3 mr-1" /> {formatDateTime(row.updatedAt || row.createdAt, false)}</div>
            </div>
        )
    }
  ];

  const tableActions = [
    // {
    //   label: 'View Details',
    //   icon: <Eye className="h-4 w-4" />,
    //   onClick: (row) => handleView(row)
    // },
    {
      label: 'Edit User',
      icon: <Edit2 className="h-4 w-4" />,
      onClick: (row) => handleEdit(row)
    }
  ];

  if (isLoading) return <UserListSkeleton />;

  return (
    <div className="p-4">
      {/* Header */}
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="rounded-lg bg-red-50 border border-red-100 hover:border-primary text-textPrimary hover:text-primary p-2 transition-colors"><FaArrowLeft /></button>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">User Management</h1>
        </div>
        {/* <button onClick={handleAdd} className="flex items-center gap-2 bg-gradient-to-r from-red-600 to-pink-600 text-white px-4 py-2 rounded-lg hover:shadow-lg transition-all">
          <Plus className="h-5 w-5" /> <span>Add User</span>
        </button> */}
      </div>

      {/* Search and Filters */}
      <div className="mb-6 space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 outline-none"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 border rounded-xl transition-colors ${showFilters ? 'bg-red-50 border-red-300 text-red-700' : 'bg-white border-gray-300'}`}
          >
            <Filter className="h-5 w-5" /> Filters
          </button>
        </div>

        {showFilters && (
          <div className="bg-white p-4 rounded-xl shadow-sm border grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 animate-in fade-in slide-in-from-top-2">
            <SelectDropdown label="Role" options={[{ roleName: 'All Roles', role: '' }, ...roles.map(r => ({ ...r, role: r.roleName }))]} value={roleFilter} onChange={setRoleFilter} valueKey="role" labelKey="roleName" />
            <SelectDropdown label="Department" options={[{ name: 'All Departments', _id: '' }, ...departments]} value={departmentFilter} onChange={setDepartmentFilter} valueKey="_id" labelKey="name" searchable />
            <SelectDropdown label="Division" options={[{ name: 'All Divisions', _id: '' }, ...divisions]} value={divisionFilter} onChange={setDivisionFilter} valueKey="_id" labelKey="name" searchable />
            <SelectDropdown label="Status" options={[{ label: 'All Status', value: '' }, { label: 'Active', value: 'active' }, { label: 'Inactive', value: 'inactive' }]} value={statusFilter} onChange={setStatusFilter} valueKey="value" labelKey="label" />
            <button onClick={() => { setRoleFilter(''); setDepartmentFilter(''); setDivisionFilter(''); setStatusFilter(''); setSearchTerm(''); }} className="text-red-600 text-sm font-medium flex items-center gap-1 hover:underline">
              <X className="h-4 w-4" /> Clear All Filters
            </button>
          </div>
        )}
      </div>

      {/* Modern Table Component */}
      <ModernTable
        columns={columns}
        data={paginatedData}
        actions={tableActions}
        selectable={false} // Change to true if you need bulk actions
        stickyColumns={1} // Makes the "User" column sticky
        pagination={{
          totalItems: filteredUsers.length,
          itemsPerPage: itemsPerPage,
          currentPage: currentPage,
          onPageChange: setCurrentPage,
          onItemsPerPageChange: (val) => { setItemsPerPage(val); setCurrentPage(1); }
        }}
      />

      {/* Empty State */}
      {!isLoading && filteredUsers.length === 0 && (
        <div className="bg-white rounded-xl border p-12 text-center mt-4">
          <Users className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900">No employees found</h3>
          <p className="text-gray-500">Try adjusting your filters or search criteria.</p>
        </div>
      )}
    </div>
  );
};

export default SecurityUsers;